import * as Icons from "@reverb-ui/icons-react";
import { Box, Heading, HStack, Text, Tooltip } from "@reverb-ui/react";
import React, { useMemo } from "react";
import { capitalize } from "utils/formatters/capitalize";
import { IconVariationEnum } from "data/constants/constants";
import { findCard, numberWithCommas } from "helper/cardsHelper";
import { HeaderCard } from "presentation/components/HeaderCard";
import EqualRondedIcon from "Icons/EqualRonded";

export interface ICardHighlightsProps {
  order: number;
  iconTitle: string;
  title: string;
  text: string;
  percentage: string;
  iconVariation: string;
  percentageVariation: string;
  previousRange: string;
  previousText: string;
  minH?: number | string;
}

type TCustomIconProps = {
  name: keyof typeof Icons;
  color?: string;
  size?: string | number;
  variant?: string;
};

enum ECardType {
  Conversion = 2,
  Bounce = 3,
}

const CustomIcon = ({ name, size, color, variant }: TCustomIconProps) => {
  const Icon = useMemo(() => Icons[name], [name]);

  return (
    <Icon variant={variant as any} color={color as any} size={size as any} />
  );
};

export const CardHighlights: React.FC<ICardHighlightsProps> = ({
  order,
  iconTitle,
  title,
  text,
  percentage,
  iconVariation,
  previousRange,
  previousText,
  percentageVariation,
  minH,
}) => {
  const cardInfo = findCard(order);
  const DescIcon = useMemo(() => {
    const isBounce = cardInfo?.cardId === ECardType.Bounce;
    if (iconVariation === IconVariationEnum.UP) {
      return (
        <CustomIcon
          name="CaretUp"
          color={isBounce ? "red.500" : "green.500"}
          variant="solid"
          size="md"
        />
      );
    } else if (iconVariation === IconVariationEnum.DOWN) {
      return (
        <CustomIcon
          name="CaretDown"
          color={isBounce ? "green.500" : "red.500"}
          variant="solid"
          size="md"
        />
      );
    } else if (iconVariation === IconVariationEnum.EQUAL) {
      return <EqualRondedIcon />;
    } else {
      return (
        <CustomIcon
          size="md"
          name={capitalize(iconVariation) as any}
          variant="solid"
          color="blue.300"
        />
      );
    }
  }, [iconVariation, title]);

  return (
    <Box p={5} bg="white" w="full" h="full" borderRadius="0.6rem" minH={minH}>
      <Tooltip
        bg="gray.100"
        color="gray.800"
        label={cardInfo?.tooltipMessage}
        isDisabled={!cardInfo?.tooltipMessage}
      >
        <Box>
          <HeaderCard title={title} iconName={iconTitle} />
          {percentage ? (
            <HStack>
              {cardInfo?.cardId === ECardType.Conversion ||
              cardInfo?.cardId === ECardType.Bounce ? (
                <>
                  <Heading
                    color="gray.800"
                    fontWeight="bold"
                    whiteSpace="nowrap"
                  >
                    {percentage}
                  </Heading>
                  <Text fontSize="2xl">({numberWithCommas(text)})</Text>
                </>
              ) : (
                <>
                  <Heading
                    color="gray.800"
                    fontWeight="bold"
                    whiteSpace="nowrap"
                  >
                    {numberWithCommas(text)}
                  </Heading>
                  <Text fontSize="2xl">({percentage})</Text>
                </>
              )}
            </HStack>
          ) : (
            <Heading color="gray.800">{numberWithCommas(text) || "-"}</Heading>
          )}
          <HStack pt={2} alignItems="top">
            <Box>{DescIcon}</Box>
            <Text fontSize="xs" fontWeight="semibold">
              {percentageVariation || previousText}
            </Text>
            <Text flex={1} color="gray.600" fontSize="xs">
              {previousRange}
            </Text>
          </HStack>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default CardHighlights;
